import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  DialogTitle,
  Stack
} from '@mui/material';

import { Close, KeyboardArrowDown } from '@mui/icons-material';

import { selectSx } from './styles';
import {
  dialogHeaderStyles,
  textFieldSx,
  menuProps
} from '../../../Common/components/Styles/InputStyles';
import CommonDialogActions from '../../../Common/components/CommonDialogActions';

const states = ['California', 'Texas', 'Florida', 'New York', 'Illinois'];

const EditOrganizationModal = ({ open, data, onClose, onSave }) => {
  const [form, setForm] = useState({
    organizationName: '',
    website: '',
    npi: '',
    billingName: '',
    taxId: '',
    billingStreet: '',
    city: '',
    state: '',
    zip: ''
  });

  useEffect(() => {
    if (open && data) {
      setForm({
        organizationName: data.organizationName || '',
        website: data.website || '',
        npi: data.npi || '',
        billingName: data.billingName || '',
        taxId: data.taxId || '',
        billingStreet: data.billingStreet || '',
        city: data.city || '',
        state: data.state || '',
        zip: data.zip || ''
      });
    }
  }, [open, data]);

  const handleChange = field => event => {
    setForm(previous => ({
      ...previous,
      [field]: event.target.value
    }));
  };

  const isValid =
    form.organizationName &&
    form.website &&
    form.npi &&
    form.billingStreet &&
    form.city &&
    form.state &&
    form.zip;

  const handleSave = () => {
    if (!isValid) {
      return;
    }

    onSave(form);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: '10px',
            overflow: 'hidden'
          }
        }
      }}>
      <DialogTitle sx={dialogHeaderStyles.dialogheader}>
        <Box>
          <Typography sx={dialogHeaderStyles.title}>
            {' '}
            Organization
          </Typography>
          <Typography sx={dialogHeaderStyles.subtitle}>
            View and update organization information.
          </Typography>
        </Box>

        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={dialogHeaderStyles.dialogheaderclose}>
          <Close sx={{ fontSize: 22 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2 }}>
        <Stack spacing={1.6} sx={{ pt: 2 }}>
          <TextField
            required
            fullWidth
            size="small"
            label="Organization Name"
            value={form.organizationName}
            onChange={handleChange('organizationName')}
            sx={textFieldSx}
          />

          <TextField
            required
            fullWidth
            size="small"
            label="Organization Website"
            value={form.website}
            onChange={handleChange('website')}
            sx={textFieldSx}
          />

          <TextField
            required
            fullWidth
            size="small"
            label="Organization NPI"
            value={form.npi}
            onChange={handleChange('npi')}
            sx={textFieldSx}
          />

          <TextField
            fullWidth
            label="Billing Name"
            size="small"
            value={form.billingName}
            onChange={handleChange('billingName')}
            sx={textFieldSx}
          />

          <TextField
            fullWidth
            label="Company Tax ID/ Sole Proprietor SS#"
            value={form.taxId}
            size="small"
            onChange={handleChange('taxId')}
            sx={textFieldSx}
          />

          <TextField
            fullWidth
            size="small"
            label="Billing Address-Street/P.O Box"
            value={form.billingStreet}
            onChange={handleChange('billingStreet')}
            sx={textFieldSx}
          />

          <TextField
            fullWidth
            label="City"
            size="small"
            value={form.city}
            onChange={handleChange('city')}
            sx={textFieldSx}
          />

          <FormControl fullWidth size="small" sx={textFieldSx}>
            <InputLabel>State</InputLabel>

            <Select
              label="State"
              value={form.state}
              onChange={handleChange('state')}
              MenuProps={menuProps}
              IconComponent={KeyboardArrowDown}>
              {states.map(state => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            size="small"
            label="Zip"
            value={form.zip}
            onChange={handleChange('zip')}
            sx={textFieldSx}
          />
        </Stack>
      </DialogContent>
      <CommonDialogActions
        onCancel={onClose}
        onSubmit={handleSave}
        submitText="Save"
        disabled={!isValid}
      />
    </Dialog>
  );
};

EditOrganizationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  data: PropTypes.shape({
    organizationName: PropTypes.string,
    website: PropTypes.string,
    npi: PropTypes.string,
    billingName: PropTypes.string,
    taxId: PropTypes.string,
    billingStreet: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string
  }),
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired
};

EditOrganizationModal.defaultProps = {
  data: null
};

export default EditOrganizationModal;
